import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/AppLayout";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PublicMobileNav } from "@/components/PublicMobileNav";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(form.identifier.trim(), form.password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back");
      const dest = r.user.role === "admin" ? "/admin" : (loc.state?.from?.pathname || "/dashboard");
      nav(dest, { replace: true });
    } else toast.error(r.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute top-4 left-4 z-20"><PublicMobileNav /></div>
      <div className="absolute top-4 right-4 z-20"><ThemeToggle /></div>
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] sm:w-[700px] max-w-[120vw] h-[300px] sm:h-[400px] bg-[#00F0FF]/15 blur-[100px] sm:blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-6 sm:mb-8"><Logo /></div>
        <div className="glass-strong rounded-2xl p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="text-[#94A3B8] text-sm mt-1">Access your client portal</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field label="Email or User ID" value={form.identifier}
                   onChange={(v) => setForm({ ...form, identifier: v })} testid="login-email" placeholder="you@email.com or X123456" />
            <Field label="Password" type="password" value={form.password}
                   onChange={(v) => setForm({ ...form, password: v })} testid="login-password" />
            <div className="text-right -mt-1">
              <Link to="/forgot-password" data-testid="link-forgot-password" className="text-xs text-[#00F0FF] hover:underline">Forgot password?</Link>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] hover:shadow-[0_0_24px_rgba(0,240,255,0.45)] transition-all disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="text-sm text-[#94A3B8] text-center mt-6">
            New here? <Link to="/register" className="text-[#00F0FF] hover:underline" data-testid="link-to-register">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-bold">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full bg-[#0A132A] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-[#475569] focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-all"
        required
      />
    </label>
  );
}
